import { get, set } from 'idb-keyval'

/**
 * The queue of orders taken while offline, and the operations needed to replay
 * them on reconnect.
 *
 * Lives in IndexedDB rather than localStorage for the same reason the query
 * cache does (see lib/idbPersister.ts): localStorage's ~5 MB quota fails
 * silently, and silent failure here means a lost sale.
 *
 * Every operation is APPEND-ONLY — create an order, add items to one. Nothing
 * here mutates or deletes server state, which is what collapses the entire
 * conflict surface to a single question at replay time: did this order reach a
 * terminal state while the terminal was dark?
 */

const STORE_KEY = 'bhookly-offline-orders-v1'

export interface OfflineLine {
  productId: string
  quantity: number
  /**
   * The product's name AT CAPTURE TIME (F-FE-N151).
   *
   * The one exception to the ids-only rule below, and it is not an exception to
   * its REASON. That rule exists to keep stale PRICES out of the queue; a name
   * is not a price and can never be resurrected as money.
   *
   * It is here because this entry becomes the reconciliation record when a
   * replay fails permanently, and that record was showing a manager
   * `1 x 806761da-7bfd-4c7c-b755-0808b2126514` while asking them to decide
   * whether to write the food off. Migration 076 asked for a frozen snapshot of
   * what was fired, immune to a later rename — which is exactly what capturing
   * the name here gives, and what resolving it afterwards cannot.
   *
   * Never sent on the replay: toReplayRequest whitelists the fields it maps, so
   * the server still reprices from the live menu and nothing cached here can
   * reach a price. Optional, so an entry queued by an older bundle still loads.
   */
  name?: string
  special_instructions?: string
  /**
   * Chosen modifier options (F-MOD-N2) — option ids and counts, nothing else.
   *
   * Ids only, deliberately: the queue is an INSTRUCTION to be re-priced when
   * the link returns, so a price or a label cached here could only ever be a
   * stale figure waiting to be resurrected. The printed kitchen ticket is the
   * opposite case — it froze its labels at capture time, because a ticket is a
   * snapshot of what was printed. Different data, both correct.
   *
   * Omitted entirely when the line has no selections, so a plain queue entry
   * stays byte-identical to what shipped before modifiers existed.
   */
  selections?: Array<{ option_id: string; quantity: number }>
}

/**
 * A tender taken while the terminal was offline (F-CTRL-N55).
 *
 * `amount` is what the cashier ACTUALLY COLLECTED, computed offline from the
 * cached menu by lib/counterPricing.ts. It is recorded, not obeyed: at replay
 * the server reprices from the live menu and the order settles at the server's
 * figure. If the two disagree the difference goes to a manager through the
 * reconciliation queue. No offline device is ever the pricing authority
 * (invariant #11).
 *
 * Deliberately no discount, void, split or loyalty: all of those need a
 * server-side bcrypt PIN check or server state, and none of them can be made
 * safe on a terminal that cannot be asked.
 */
export interface OfflinePayment {
  /**
   * Cash, card or online. Online is here because staff really do take transfers
   * during an outage — the customer sends it from their own phone on their own
   * data, which is unaffected by the restaurant's dead link.
   *
   * The tender is not cosmetic: `tax_rate_cash` / `tax_rate_card` /
   * `tax_rate_online` are separate settings, so it decides the rate and
   * therefore the amount due (invariant #11).
   */
  method: 'cash' | 'card' | 'online'
  /** What was collected, in tenant currency. */
  amount: number
  /** Online only — which provider (Easypaisa/JazzCash/…), for the reports breakdown. */
  subMethod?: string
  /** Cash only — the note handed over, so the slip can print the change. */
  tendered?: number
}

export interface OfflineOrderDraft {
  /**
   * The identifier printed on the kitchen ticket (F-CTRL-N52).
   *
   * For a new offline order this is a locally-minted token ("OFF-A3-7") and the
   * server allocates the real order number when the entry replays. For an append
   * to an order that already exists on the server it is that order's real
   * number, because that ticket really does belong to a numbered order.
   */
  ticketRef: string
  /**
   * LEGACY: a reserved order number, from an entry queued by a pre-N52 bundle
   * that upgraded mid-outage. Sent on as `order_number` so those slips keep the
   * number they printed; the server validates it before honouring it. Absent on
   * everything this bundle queues.
   */
  orderNumber?: string
  /** Epoch ms when the cashier committed the order. Drives business_day_id. */
  capturedAt: number
  orderType: string
  tableId: string | null
  lines: OfflineLine[]
  userId: string
  /**
   * Guests at the table (F-CTRL-N67). Set by the floor screen; the counter
   * leaves it absent. Stamped on the order at replay.
   */
  guestCount?: number
  /**
   * Which screen took the order — the floor ('server') or the till ('pos').
   * Advisory on the wire: the server derives the real provenance from the
   * caller's role and never reads this. Absent means 'pos'.
   */
  source?: 'pos' | 'server'
  /**
   * Set when the cashier took payment during the outage (F-CTRL-N55). Absent on
   * an order held open to be settled after reconnect, which is still the normal
   * dine-in case.
   */
  payment?: OfflinePayment
  /**
   * Lines the cashier voided AFTER the kitchen ticket printed (F-CTRL-N56).
   *
   * Kept rather than silently discarded: the kitchen holds paper for these, a
   * void chit was printed to cancel them, and the difference between what the
   * ticket said and what the order becomes is the only record that any of it
   * happened. The order replays WITHOUT them — they were never on the server —
   * so this is the local audit trail, not an instruction.
   */
  voids?: Array<{
    productId: string
    quantity: number
    /** Epoch ms — when the cashier voided it, not when it was rung. */
    voidedAt: number
    /** Who was signed in at the terminal. No PIN was verified; see F-CTRL-N56. */
    voidedBy: string
  }>
  /** Set when adding to an order that already exists on the server. */
  targetOrderId?: string
  /** Preserved on rehydrate so a replay retry reuses the same key. */
  opId?: string
}

/**
 * `discarded` is a TERMINAL state for an entry the server will never accept and
 * that has nothing left to bill (F-FE-N160).
 *
 * It exists because the other three could not describe an entry whose every line
 * was voided: `pending` retried it forever, `synced` is a lie, and `unreconciled`
 * would put a bill-nothing order in front of a manager and block Day Close on it.
 *
 * The row is KEPT rather than deleted so its `voids` audit trail survives — that
 * is the only local record that a void chit was printed for each line.
 */
export type OfflineOrderStatus = 'pending' | 'synced' | 'unreconciled' | 'discarded'

export interface OfflineOrder extends OfflineOrderDraft {
  opId: string
  kind: 'create' | 'add_items'
  status: OfflineOrderStatus
  attempts: number
  lastError: string | null
  /**
   * Epoch ms before which this entry should not be retried (F-FE-N75).
   *
   * `attempts` was incremented on every failure and then read by nothing, so a
   * retry was neither backed off nor counted for any purpose — the queue simply
   * re-attempted everything at whatever rate something happened to call the
   * drain. Absent on an entry that has never failed, and on entries written by
   * an older bundle, both of which are due immediately.
   */
  nextAttemptAt?: number
}

/**
 * How long to wait before retrying an entry that has failed `attempts` times.
 *
 * Exponential from 5s, capped at 5 minutes. The cap matters more than the curve:
 * the commonest transient failure is `day_not_open`, which clears when a manager
 * opens the day at the counter — a human action on a human timescale. Backing
 * off past that would leave food cooked and unsynced long after the blocker was
 * cleared; not backing off at all would hammer a backend that has just come back
 * with a request that is going to fail for the same reason.
 *
 * There is deliberately NO attempt cap. Every entry in this queue is food that
 * already left the kitchen; giving up on one would be the silent drop the whole
 * subsystem exists to prevent. An entry that can never succeed is moved to the
 * reconciliation queue by the classifier instead, where a human decides.
 */
export function retryBackoffMs(attempts: number): number {
  const base = 5_000
  const max = 5 * 60_000
  if (attempts <= 0) return 0
  return Math.min(base * 2 ** (attempts - 1), max)
}

/** Distinct, stable once assigned, and readable in a log line. */
export function nextOpId(userId: string, seq: number): string {
  return `offline:${userId}:${seq}:${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Build a queue entry from a draft.
 *
 * Lines are rebuilt field-by-field rather than spread, so a caller that hands
 * over a richer object (a cart line carrying the whole Product, say) cannot
 * accidentally persist a base64 image or a stale price.
 */
export function makeOfflineOrder(d: OfflineOrderDraft): OfflineOrder {
  return {
    ticketRef: d.ticketRef,
    ...(d.orderNumber ? { orderNumber: d.orderNumber } : {}),
    ...(d.payment ? { payment: d.payment } : {}),
    ...(d.voids && d.voids.length > 0 ? { voids: d.voids } : {}),
    capturedAt: d.capturedAt,
    orderType: d.orderType,
    tableId: d.tableId,
    userId: d.userId,
    ...(d.guestCount != null ? { guestCount: d.guestCount } : {}),
    ...(d.source ? { source: d.source } : {}),
    ...(d.targetOrderId ? { targetOrderId: d.targetOrderId } : {}),
    opId: d.opId ?? nextOpId(d.userId, d.capturedAt),
    kind: d.targetOrderId ? 'add_items' : 'create',
    status: 'pending',
    attempts: 0,
    lastError: null,
    lines: d.lines.map((l) => ({
      productId: l.productId,
      quantity: l.quantity,
      ...(l.name ? { name: l.name } : {}),
      ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
      // Rebuilt field-by-field for the same reason as the line itself: a cart
      // selection may carry a resolved label and price for rendering, and
      // neither belongs in the queue.
      ...(l.selections && l.selections.length > 0
        ? {
            selections: l.selections.map((sel) => ({
              option_id: sel.option_id,
              quantity: sel.quantity,
            })),
          }
        : {}),
    })),
  }
}

/**
 * Error codes that will NEVER succeed on retry, so the operation must stop
 * being retried and go to the reconciliation queue for a human decision.
 *
 * `day_not_open` is deliberately absent: a manager can still open the day, at
 * which point parked orders flush in. Anything unrecognised is also absent, so
 * an unknown failure retries rather than silently discarding a ticket the
 * kitchen has already cooked.
 *
 * F-FE-N160 widened this. It used to hold four codes, and the "an entry that can
 * never succeed is moved to the reconciliation queue by the classifier" promise
 * in retryBackoffMs above was therefore only true for those four. The replay
 * endpoint refuses on several other codes that are just as permanent — every one
 * of them a 4xx, meaning the server understood the request and will not change
 * its mind — and each of those entries retried at the 5-minute cap forever with
 * no way for anyone to clear it.
 *
 * Confirmed in production at Mizaj: a single entry 400'd every five minutes for
 * ~19 hours while a sibling entry in the same drain synced fine. See
 * audit/MIZAJ_OFFLINE_TRAY_STUCK_2026-09-23.md §10.
 *
 * Everything here means FOOD WAS FIRED AND CANNOT BE BILLED, which is precisely
 * what the reconciliation queue is for. The one permanent refusal that does NOT
 * mean that — `empty_order` — is handled by NOTHING_TO_BILL_REPLAY_ERRORS below.
 */
const TERMINAL_REPLAY_ERRORS = new Set([
  'order_not_editable',
  'order_not_found',
  'table_has_active_order',
  'offline_order_duplicate',
  // F-FE-N160 — permanent 4xx refusals from the replay/create path.
  'product_not_found',
  'invalid_product_id',
  'invalid_option_id',
  'missing_captured_at',
  'order_type_disabled',
  'delivery_details_required',
  'server_orders_dine_in_only',
])

export function isTerminalReplayError(code: string | null): boolean {
  return code != null && TERMINAL_REPLAY_ERRORS.has(code)
}

/**
 * Permanent refusals where there is NOTHING LEFT TO BILL (F-FE-N160).
 *
 * `empty_order` is the whole set, and it is separated from the reconciliation
 * codes above on purpose. An entry reaches it only by having every one of its
 * lines voided — and each of those voids printed a chit telling the kitchen to
 * stop. So no food is outstanding, nobody is owed money, and there is nothing
 * for a manager to decide.
 *
 * Routing it to `unreconciled` would therefore be worse than the bug it fixes:
 * the tray would read "Needs a manager", the server reconciliation queue would
 * gain a row worth zero, and Day Close would block on it. Worse still, the
 * reconciliation endpoint itself rejects an empty line list
 * (offline_reconciliation.go:69), so the handover would fail and the entry would
 * fall straight back to `pending` — the exact loop we are trying to leave.
 */
const NOTHING_TO_BILL_REPLAY_ERRORS = new Set(['empty_order'])

export function isNothingToBillReplayError(code: string | null): boolean {
  return code != null && NOTHING_TO_BILL_REPLAY_ERRORS.has(code)
}

export async function listOfflineOrders(): Promise<OfflineOrder[]> {
  return (await get<OfflineOrder[]>(STORE_KEY)) ?? []
}

export async function enqueueOfflineOrder(d: OfflineOrderDraft): Promise<OfflineOrder> {
  const o = makeOfflineOrder(d)
  const all = await listOfflineOrders()
  await set(STORE_KEY, [...all, o])
  return o
}

export async function updateOfflineOrder(
  opId: string,
  patch: Partial<OfflineOrder>,
): Promise<void> {
  const all = await listOfflineOrders()
  await set(
    STORE_KEY,
    all.map((o) => (o.opId === opId ? { ...o, ...patch } : o)),
  )
}

/**
 * Append lines to an order that is still waiting to sync (F-FE-N31).
 *
 * During an outage a table keeps ordering. Without this the cashier could take
 * the first round and then nothing else for that table until the connection
 * returned — every later round would have had to be rung as a separate order,
 * which splits one bill into several and leaves the floor reconciling by hand.
 *
 * Editing a queued order is safe in exactly one window: while it is still
 * `pending`. Once replay has posted it the server owns it, and the ordinary
 * add-items path — which has the idempotency guard (F-CTRL-N37) — is the only
 * correct way in. Appending to the local copy after that point would queue food
 * against an order that will never be replayed again, so it would never be
 * billed.
 *
 * Returns the updated entry, or null when the entry is gone or has left
 * `pending`. A null is NOT a no-op: the caller still holds items the cashier
 * typed, and must route them to the real order rather than drop them.
 *
 * Lines are appended, never merged into matching ones. A later round is its own
 * round — the same rows an online add-items call would have produced.
 */
export async function appendLinesToPendingOfflineOrder(
  opId: string,
  lines: OfflineLine[],
): Promise<OfflineOrder | null> {
  const all = await listOfflineOrders()
  const target = all.find((o) => o.opId === opId)
  if (!target || target.status !== 'pending') return null

  const updated: OfflineOrder = {
    ...target,
    // Rebuilt field-by-field for the same reason makeOfflineOrder does it: a
    // caller handing over a cart line must not be able to persist a Product.
    lines: [
      ...target.lines,
      ...lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        ...(l.name ? { name: l.name } : {}),
        ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
        // F-MOD-N8: selections MUST be carried here too.
        //
        // This rebuild originally omitted them while makeOfflineOrder carried
        // them, so modifiers chosen on the FIRST round of an offline order were
        // billed and modifiers chosen on every later round were not. The slip
        // still printed them — it is built from the caller's lines, not from
        // this entry — so the kitchen made the add-on, the guest ate it, and the
        // replayed order had no line to charge for it. Silent, and only visible
        // as an unexplained margin drift.
        ...(l.selections && l.selections.length > 0
          ? {
              selections: l.selections.map((sel) => ({
                option_id: sel.option_id,
                quantity: sel.quantity,
              })),
            }
          : {}),
      })),
    ],
  }
  await set(
    STORE_KEY,
    all.map((o) => (o.opId === opId ? updated : o)),
  )
  return updated
}

/**
 * Remove a line from an order still queued on this device (F-CTRL-N56).
 *
 * Safe in a way an online void is not: this order has NEVER reached the server,
 * so there is no live state to mutate, no order_item to reverse, and nothing for
 * the append-only replay model to conflict with. It is an edit to a draft this
 * terminal owns outright — which is why the owner's decision to let a cashier do
 * it without a manager PIN costs no control that existed in the first place. A
 * PIN could not be verified offline anyway (staffpin.Identify is bcrypt against
 * the database).
 *
 * What it is NOT safe about is the kitchen: a ticket for this line is already on
 * the pass. The caller MUST print a void chit. This function only edits state.
 *
 * Returns the updated entry, or null when the entry is gone, has left `pending`,
 * or the index does not exist. Null is never a silent no-op for the caller: if
 * the order has already synced, the void has to go through the server.
 */
export async function voidLineFromPendingOfflineOrder(
  opId: string,
  lineIndex: number,
  voidedBy: string,
  now: number,
): Promise<OfflineOrder | null> {
  const all = await listOfflineOrders()
  const target = all.find((o) => o.opId === opId)
  if (!target || target.status !== 'pending') return null
  const line = target.lines[lineIndex]
  if (!line) return null

  const remaining = target.lines.filter((_, i) => i !== lineIndex)

  const updated: OfflineOrder = {
    ...target,
    lines: remaining,
    // F-FE-N160: voiding the LAST line finalises the entry here rather than
    // leaving a zero-line `pending` row for the drain to discover.
    //
    // A zero-line entry is unsendable: toReplayRequest posts `lines: []`, the
    // endpoint refuses it with `empty_order` (offline_replay.go:154), and the
    // classifier used to call that retryable — so it re-posted every five
    // minutes forever and the tray offered no way to remove it. That is the
    // Mizaj incident. Nothing is lost by stopping here: the order never reached
    // the server, and every line leaves with a printed void chit.
    //
    // `voids` is deliberately retained, and the row is not deleted, because it
    // is the only local record that those chits were printed.
    ...(remaining.length === 0 ? { status: 'discarded' as const, lastError: null } : {}),
    voids: [
      ...(target.voids ?? []),
      {
        productId: line.productId,
        quantity: line.quantity,
        voidedAt: now,
        voidedBy,
      },
    ],
  }
  await set(
    STORE_KEY,
    all.map((o) => (o.opId === opId ? updated : o)),
  )
  return updated
}

/**
 * Drop successfully-synced entries. Pending and unreconciled ones stay — an
 * unreconciled entry is unresolved work, and dropping it locally would hide
 * food that left the kitchen unbilled.
 */
export async function pruneSyncedOfflineOrders(): Promise<void> {
  const all = await listOfflineOrders()
  await set(
    STORE_KEY,
    all.filter((o) => o.status !== 'synced'),
  )
}

/** Entries still waiting to post, oldest first — a create must precede its add-items. */
export async function pendingOfflineOrders(): Promise<OfflineOrder[]> {
  return (await listOfflineOrders())
    .filter((o) => o.status === 'pending')
    .sort((a, b) => a.capturedAt - b.capturedAt)
}
